import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { useMedia } from 'react-use';
import styled, { css } from 'styled-components';
import { BREAKPOINTS } from '../../../../../constants/layout.mjs';
import '../../../../../constants/modelCompareData/assets/check.svg.mjs';
import '../../../../../constants/modelCompareData/assets/minus.svg.mjs';
import { MutatedPropertyType } from '../../../../../constants/modelCompareData/types.mjs';
import { theme } from '../../../../../constants/theme.mjs';
import '../../../../../contexts/MenuContext/index.mjs';
import '../../../../Reveal/index.mjs';
import '../../../../StyleableSvgImage/index.mjs';
import { MOBILE_BREAKPOINT } from '../../CompareTable.styles.mjs';
import './StickyGrid/index.mjs';
import { StyleableSvgImage } from '../../../../StyleableSvgImage/StyleableSvgImage.mjs';
import { Reveal } from '../../../../Reveal/Reveal.mjs';
import { useMenu } from '../../../../../contexts/MenuContext/context.mjs';
import checkIconSvg__default from "../../../../../assets/check2.svg";
import minusIconSvg__default from "../../../../../assets/minus.svg";
import { StickyGridItem } from './StickyGrid/StickyGridItem.mjs';
"use strict";
function PropertyValueGridItem({
  productProperty,
  includeProperty,
  property,
  isLastProduct,
  index
}) {
  const isMobile = useMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
  if (includeProperty) {
    return /* @__PURE__ */jsx(InlineGridItem, {
      children: isMobile ? /* @__PURE__ */jsxs(Fragment, {
        children: [/* @__PURE__ */jsx(PropertyIcon, {
          children: /* @__PURE__ */jsx(StyleableSvgImage, {
            width: 30,
            height: 30,
            src: property.icon
          })
        }), /* @__PURE__ */jsx(PropertyTitle, {
          children: property.title
        }), /* @__PURE__ */jsx(Spacer, {}), /* @__PURE__ */jsx(Value, {
          $disableOverflow: productProperty?.value.type === MutatedPropertyType.Boolean,
          children: /* @__PURE__ */jsx(PropertyValueComponent, {
            productProperty
          })
        })]
      }) : /* @__PURE__ */jsxs(Reveal, {
        index,
        children: [/* @__PURE__ */jsx(PropertyIcon, {
          children: /* @__PURE__ */jsx(StyleableSvgImage, {
            width: 30,
            height: 30,
            src: property.icon
          })
        }), /* @__PURE__ */jsx(PropertyTitle, {
          children: property.title
        }), /* @__PURE__ */jsx(Spacer, {}), /* @__PURE__ */jsx(Value, {
          $disableOverflow: productProperty?.value.type === MutatedPropertyType.Boolean,
          children: /* @__PURE__ */jsx(PropertyValueComponent, {
            productProperty
          })
        })]
      })
    });
  }
  return /* @__PURE__ */jsx(GridItem, {
    $isLastProduct: isLastProduct,
    children: /* @__PURE__ */jsx(Reveal, {
      index,
      children: /* @__PURE__ */jsx(PropertyValueComponent, {
        productProperty
      })
    })
  });
}
function PropertyValueComponent({
  productProperty
}) {
  const {
    languageDirection,
    storeId
  } = useMenu();
  const asterisks = useMemo(() => {
    const count = productProperty?.value.asterisk || 0;
    return count > 0 ? "*".repeat(count) : null;
  }, [productProperty?.value.asterisk]);
  if (!productProperty?.value) return null;
  const {
    type,
    value
  } = productProperty.value;
  switch (type) {
    case MutatedPropertyType.Custom:
      return /* @__PURE__ */jsxs(Fragment, {
        children: [value, asterisks]
      });
    case MutatedPropertyType.Boolean:
      return /* @__PURE__ */jsxs(BooleanWrap, {
        children: [/* @__PURE__ */jsx(StyleableSvgImage, {
          width: value ? 16 : 11,
          height: value ? 12 : 8,
          src: value ? checkIconSvg__default : minusIconSvg__default
        }), asterisks && /* @__PURE__ */jsx(BooleanAsterisks, {
          children: asterisks
        })]
      });
    case MutatedPropertyType.String:
      {
        const value2 = typeof productProperty.value.value === "string" ? productProperty.value.value : productProperty.value.value(storeId);
        return /* @__PURE__ */jsx(Stack, {
          children: value2.split("\n").map((row, i, arr) => {
            const isLastRow = i === arr.length - 1;
            return /* @__PURE__ */jsxs("div", {
              dir: languageDirection,
              children: [row, isLastRow && asterisks]
            }, i);
          })
        });
      }
    default:
      return null;
  }
}
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  letter-spacing: -0.28px;
  color: ${theme.colors.black65};

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 12px;
  }
`;
const Value = styled.div`
  text-align: right;

  ${({
  $disableOverflow
}) => !$disableOverflow && css`
      overflow: hidden;
    `}
  text-overflow: ellipsis;
  min-width: min-content;

  div {
    ${({
  $disableOverflow
}) => !$disableOverflow && css`
        overflow: hidden;
      `}

    text-overflow: ellipsis;
  }
`;
const borderStyles = `1px solid ${theme.colors.athensGreyDark}`;
const GridItem = styled(StickyGridItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #000000a6;
  text-align: center;
  padding: 16px;
  border-bottom: ${borderStyles};
  border-left: ${borderStyles};

  ${({
  $isLastProduct
}) => $isLastProduct && css`
      border-right: ${borderStyles};
    `}
`;
const InlineGridItem = styled(StickyGridItem)`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px;
  overflow: hidden;
  border-bottom: 1px solid ${theme.colors.athensGreyDark};
`;
const PropertyTitle = styled.div`
  font-size: 14px;
  font-weight: ${theme.fontWeight.bold};
  max-width: 45%;
  line-height: ${17 / 14};
`;
const PropertyIcon = styled.div`
  width: 30px;
  height: 30px;
`;
const Spacer = styled.div`
  flex: 1;
`;
const BooleanWrap = styled.div`
  position: relative;
  overflow: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5.5px;
  }
`;
const BooleanAsterisks = styled.div`
  position: absolute;
  font-size: 14px;
  line-height: 16.8px;

  top: -4.5px;
  left: calc(100% + 5.5px);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: static;

    margin-top: -4.5px;
  }
`;
export { PropertyValueGridItem };
//# sourceMappingURL=PropertyValueGridItem.mjs.map
